import { HttpClient } from '@angular/common/http';
import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { catchError, share, shareReplay, startWith } from 'rxjs/operators';

@Pipe({
  name: 'authenticatedImage',
  pure: true
})
export class AuthenticatedImagePipe implements PipeTransform, OnDestroy {

  // 1×1pxの透明な画像を用意しておき、画像URLが空のときに表示する。
  private transparentImageUrl = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  // URLとSafeUrlのマッピングをキャッシュする。これにより、同じURLに対して複数回HTTPリクエストが発生するのを防ぐ。
  private static imageCache = new Map<string, SafeUrl>();
  private static rawUrlCache = new Map<string, string>();
  private static loadingCache = new Map<string, Observable<SafeUrl>>();

  // 最大キャッシュ件数
  private static MAX_CACHE_SIZE = 100;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  transform(signedImageUrl: string, src: string): Observable<SafeUrl> {
    if (!signedImageUrl) return of(this.sanitizer.bypassSecurityTrustUrl(this.transparentImageUrl));

    // キャッシュに存在する場合はキャッシュからSafeUrlを返す。
    if (AuthenticatedImagePipe.imageCache.has(src)) {
      console.info(`Cache hit for URL: ${src}`);
      const safeUrl = AuthenticatedImagePipe.imageCache.get(src)!;
      const rawUrl = AuthenticatedImagePipe.rawUrlCache.get(src)!;
      // キャッシュの使用頻度を更新するため、一度キャッシュから削除して再度追加する。
      AuthenticatedImagePipe.imageCache.delete(src);
      AuthenticatedImagePipe.rawUrlCache.delete(src);
      AuthenticatedImagePipe.imageCache.set(src, safeUrl);
      AuthenticatedImagePipe.rawUrlCache.set(src, rawUrl);
      return of(safeUrl);
    }

    // 現在ロード中のURLがある場合は、そのObservableを返す。
    if (AuthenticatedImagePipe.loadingCache.has(src)) {
      console.info(`Already loading URL: ${src}. Returning existing Observable.`);
      return AuthenticatedImagePipe.loadingCache.get(src)!;
    }

    console.info(`Cache miss for URL: ${src}. Fetching image...`);
    const request = this.http.get(signedImageUrl, { responseType: 'blob' }).pipe(
      map(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
        // キャッシュの上限を超える場合は、最も古いキャッシュを削除する。
        if (AuthenticatedImagePipe.imageCache.size >= AuthenticatedImagePipe.MAX_CACHE_SIZE) {
          const firstKey = AuthenticatedImagePipe.imageCache.keys().next().value;
          if (firstKey) {
            console.info(`Cache limit exceeded. Evicting oldest cache entry for URL: ${firstKey}`);
            URL.revokeObjectURL(AuthenticatedImagePipe.rawUrlCache.get(firstKey)!);
            AuthenticatedImagePipe.imageCache.delete(firstKey);
            AuthenticatedImagePipe.rawUrlCache.delete(firstKey);
            console.info(`Cache limit exceeded. Evicted src: ${firstKey}`);
          }
        }

        // objectUrlをキャッシュに保存する。
        AuthenticatedImagePipe.rawUrlCache.set(src, objectUrl);
        // キャッシュにURLとSafeUrlのマッピングを保存する。
        AuthenticatedImagePipe.imageCache.set(src, safeUrl);
        // ロードが完了したらloadingCacheから削除する
        AuthenticatedImagePipe.loadingCache.delete(src);
        return safeUrl;
      }),
      catchError((err) => {
        AuthenticatedImagePipe.loadingCache.delete(src);
        console.error(`Failed to load image from URL: ${signedImageUrl}. Error:`, err);
        // 呼び出し元でsignedUrlを再取得させるため、nullを返すことで、画像が読み込めなかったことを示す。
        return of(null);
      }),
      shareReplay(1), // 複数の購読者が同じHTTPリクエストを共有できるようにする
      startWith(this.sanitizer.bypassSecurityTrustUrl(this.transparentImageUrl)), // ロード中は透明な画像を表示する
    );

    // ロード中のURLとObservableをキャッシュに保存する。
    AuthenticatedImagePipe.loadingCache.set(src, request);
    return request;
  }

  static clearCache() {
    console.info(`Clearing image cache. count: ${this.rawUrlCache.size}`);
    // キャッシュ内のすべてのobject URLを解放する。
    AuthenticatedImagePipe.rawUrlCache.forEach(url => URL.revokeObjectURL(url));

    // 各Mapをクリアする。
    AuthenticatedImagePipe.rawUrlCache.clear();
    AuthenticatedImagePipe.imageCache.clear();
    AuthenticatedImagePipe.loadingCache.clear();
  }

  ngOnDestroy() {
  }
}